import type { Editor } from 'hugerte'
import tippy, { type Instance, type Props } from 'tippy.js'

/** Explanations for the upload options in HugeRTE's "Insert/Edit Media" dialog. */
export interface MediaDialogTooltips {
  general: string
  embed: string
  advanced: string
  source: string
  alternativeSource: string
  poster: string
}

/**
 * The media dialog has no supported extension point, so it is decorated after it
 * opens. `mceMedia` is its only entry point and the command dispatches once the
 * dialog is attached, which makes it a reliable — and localisation-proof — signal.
 */
const MEDIA_DIALOG_COMMAND = 'mcemedia'
const DIALOG_SELECTOR = '.tox-dialog'
const TAB_SELECTOR = '.tox-dialog__body-nav .tox-dialog__body-nav-item'
const TAB_CONTENT_SELECTOR = '.tox-dialog__body-content'
const FIELD_SELECTOR = '.tox-form__group'
const FIELD_LABEL_SELECTOR = '.tox-label'

const TOOLTIP_OPTIONS: Props = {
  allowHTML: false,
  arrow: true,
  // The dialog body scrolls, and the default boundary would clamp the tooltip
  // inside it — straight over the field it is explaining.
  boundary: 'viewport',
  theme: 'default',
  trigger: 'mouseenter focus',
}

const labelOf = (field: HTMLElement): string =>
  field.querySelector(FIELD_LABEL_SELECTOR)?.textContent?.trim() ?? ''

const createTooltip = (
  element: HTMLElement,
  content: string,
  placement: Props['placement']
): Instance => {
  const created = tippy(element, { ...TOOLTIP_OPTIONS, content, placement })

  return Array.isArray(created) ? created[0] : created
}

/**
 * Explains the upload options in the media dialog opened from the editor's media
 * toolbar button. Does nothing when the dialog markup no longer matches, so the
 * dialog keeps working regardless.
 */
export const attachMediaDialogTooltips = (
  editor: Editor,
  tooltips: MediaDialogTooltips
): void => {
  /**
   * Tabs are matched by position rather than by label or name: the tab name never
   * reaches the DOM, the plugin's "Embed" tab has no name to begin with, and the
   * order — general, embed, then advanced when the alternative source and poster
   * fields are enabled — is fixed. Fields carry a label, so they go by that.
   */
  const contentInTabOrder = [
    tooltips.general,
    tooltips.embed,
    tooltips.advanced,
  ]
  const contentByFieldLabel: Record<string, string> = {
    Source: tooltips.source,
    'Alternative source URL': tooltips.alternativeSource,
    'Media poster (Image URL)': tooltips.poster,
  }

  /** Keyed by element, because the dialog reuses both tabs and field rows. */
  const instances = new Map<HTMLElement, Instance>()
  let fieldObserver: MutationObserver | null = null

  const describeTabs = (dialog: HTMLElement) => {
    dialog.querySelectorAll<HTMLElement>(TAB_SELECTOR).forEach((tab, index) => {
      const content = contentInTabOrder[index]

      if (content) {
        instances.set(tab, createTooltip(tab, content, 'left'))
      }
    })
  }

  /**
   * Switching tabs reuses the field rows and patches their contents, so a row's
   * label — and therefore the explanation it needs — changes underneath us. Each
   * row is left alone unless its label moved on. Tabs are never revisited here:
   * they are not field rows.
   */
  const describeFields = (dialog: HTMLElement) => {
    for (const field of dialog.querySelectorAll<HTMLElement>(FIELD_SELECTOR)) {
      const content = contentByFieldLabel[labelOf(field)]
      const existing = instances.get(field)

      if (existing?.props.content === content) {
        continue
      }

      existing?.destroy()
      instances.delete(field)

      if (content) {
        instances.set(field, createTooltip(field, content, 'right'))
      }
    }
  }

  /**
   * The whole subtree is watched because a tab switch surfaces as a text swap
   * inside each label rather than as a change to the tab view's own children.
   */
  const observeFields = (dialog: HTMLElement) => {
    const tabContent = dialog.querySelector(TAB_CONTENT_SELECTOR)

    if (!tabContent) {
      return
    }

    fieldObserver = new MutationObserver(() => describeFields(dialog))
    fieldObserver.observe(tabContent, { childList: true, subtree: true })
  }

  const teardown = () => {
    fieldObserver?.disconnect()

    for (const instance of instances.values()) {
      instance.destroy()
    }
    instances.clear()
  }

  editor.on('ExecCommand', ({ command }: { command: string }) => {
    if (command.toLowerCase() !== MEDIA_DIALOG_COMMAND) {
      return
    }

    teardown()

    const dialogs = document.querySelectorAll<HTMLElement>(DIALOG_SELECTOR)
    const dialog = dialogs[dialogs.length - 1]

    if (!dialog) {
      return
    }

    describeTabs(dialog)
    describeFields(dialog)
    observeFields(dialog)
  })

  editor.on('CloseWindow', teardown)
  editor.on('remove', teardown)
}
